import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useWorkplaceUser } from 'module/documents/utils';
import { NotificationSetting } from './settingsAccountTab/NotificationSetting';
import GeneralSettings from './settingsGameMasterTabs/GeneralSetting/GeneralSettings';
import DashBoardSetting from './settingsGameMasterTabs/DashBoardSetting';
import ScoreCardSetting from './settingsGameMasterTabs/ScoreCardSetting';
import GuestConnectSetting from './settingsGameMasterTabs/GuestConnectSetting';
import ChecklistSetting from './settingsGameMasterTabs/CheckListSetting/ChecklistSetting';
import TodoSetting from './settingsGameMasterTabs/TodoSettings/TodoSetting';
import LogbooksSetting from './settingsGameMasterTabs/LogbooksSetting';
import EventsSetting from './settingsGameMasterTabs/EventsSetting';
import SchedulerSetting from './settingsGameMasterTabs/SchedulerSetting';
import OnboardingSetting from './settingsGameMasterTabs/OnboardingSettings';
import BoradcastSetting from './settingsGameMasterTabs/BoradcastSetting';
import GameMasterSetting from './settingsGameMasterTabs/GamemasterSetting/GameMasterSetting';
import { ProfileForm } from './settingsAccountTab/ProfileFormSetting';
import { SecuritySettings } from './settingsAccountTab/SecuritySettings';
import { EmployeeDocuments } from './settingsAccountTab/EmployeeDocuments';
import usePermissionCheck from 'core/hooks/usePermissionCheck';
import { useQueryClient } from 'react-query';
import { GetGameMasterData } from 'core/API/gameMaster';
import styles from './Setting.module.scss';
import { SETTINGS_SCROLL_CONTAINER_ID, useResetSettingsScroll } from './utils/resetSettingsScroll';
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

interface SettingsTab {
  name: string;
  label: string;
  resource: string;
  alwaysShow?: boolean;
  requiresProduct?: string;
}

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#f50057' },
  },
});

const ACCOUNT_TABS: SettingsTab[] = [
  { name: 'Profile', label: 'Profile', resource: 'Profile' },
  { name: 'Notifications', label: 'Notifications', resource: 'Notifications' },
  { name: 'Security', label: 'Security', resource: 'Security' },
  { name: 'Policy Hub', label: 'Policy Hub', resource: 'Policy Hub', requiresProduct: 'Onboarding' },
];

const PRODUCT_TABS: SettingsTab[] = [
  { name: 'General', label: 'General', resource: 'General', alwaysShow: true },
  { name: 'Dashboard', label: 'Home', resource: 'Home' },
  { name: 'Scorecard', label: 'Scorecard', resource: 'Scorecard' },
  { name: 'Guest Connect', label: 'Guest Connect', resource: 'Guest Connect' },
  { name: 'Checklist', label: 'Checklist', resource: 'Checklist' },
  { name: 'To-Do', label: 'TODO', resource: 'To-Do' },
  { name: 'Logbook', label: 'Logbook', resource: 'Logbook' },
  { name: 'Events', label: 'Events', resource: 'Events' },
  { name: 'Schedule', label: 'Scheduler', resource: 'Schedule' },
  { name: 'Onboarding', label: 'Onboarding', resource: 'Onboarding' },
  { name: 'Broadcast', label: 'Broadcast', resource: 'Broadcast' },
  { name: 'GameMaster', label: 'Game Master', resource: 'GameMaster' }
];

const Sidebar = ({ setActiveTab, activeTab }) => {
  const { userOrg } = useWorkplaceUser();
  const { hasViewPermission } = usePermissionCheck();
  const products = userOrg?.products || [];
  // console.log("products", products);
  const isProductAvailable = (productName: string) => {
    return products.some((product) => product.label === productName);
  };

  return (
    <Grid
      item
      xs={12}
      md={3}
      className={styles.settingsSidebar}
      sx={{
        display: 'flex',
        justifyContent: 'start',
        flexDirection: 'column',
        gap: 2,
        padding: 2,
      }}
    >
      {/* First Box with Heading "Account" */}
      <Box sx={{ backgroundColor: '#fff', padding: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ marginBottom: 1 }}>
          Account
        </Typography>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {ACCOUNT_TABS.map((tab) => {
            if (tab.requiresProduct && !isProductAvailable(tab.requiresProduct)) return null;
            if (!tab.alwaysShow && !hasViewPermission(tab.resource)) return null;
            return (
              <li
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                style={{
                  padding: '10px',
                  cursor: 'pointer',
                  backgroundColor: activeTab === tab.name ? '#446388' : 'transparent',
                  color: activeTab === tab.name ? '#fff' : '#000',
                  borderRadius: '4px',
                }}
              >
                {tab.name}
              </li>
            );
          })}
        </ul>
      </Box>

      {/* Second Box with Organization Settings */}
      <Box sx={{ backgroundColor: '#fff', padding: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ marginBottom: 1 }}>
          {userOrg?.name}
        </Typography>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {PRODUCT_TABS.map((tab) => 
            (tab.alwaysShow || isProductAvailable(tab.label)) && (
              <li
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                style={{
                  padding: '10px',
                  cursor: 'pointer',
                  backgroundColor: activeTab === tab.name ? '#446388' : 'transparent',
                  color: activeTab === tab.name ? '#fff' : '#000',
                  borderRadius: '4px',
                }}
              >
                {tab.name}
              </li>
            )
          )}
        </ul>
      </Box>
    </Grid>
  );
};

const DisplaySettings = () => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h5">Display Settings</Typography>
      <p>
        Lorem ipsum dolor sit amet consectetur adipisicing elit. Aliquid fugiat earum, maiores odit
        non in sequi mollitia quo est laboriosam deserunt quasi natus quibusdam esse laborum
        deleniti, amet ut ab commodi, sed ex rerum velit! Non voluptatibus mollitia, minima nemo
        pariatur velit saepe blanditiis laudantium explicabo id veniam, quod odit.
      </p>
    </CardContent>
  </Card>
);

export default function Setting() {
  const { userOrg } = useWorkplaceUser();
  const { hasViewPermission } = usePermissionCheck();
  const queryClient = useQueryClient();
  const products = userOrg?.products || [];
  
  const isProductAvailable = (productName: string) => {
    return products.some((product) => product.label === productName);
  };

  const [activeTab, setActiveTab] = useState(() => {
    // const savedTab = localStorage.getItem('activeTab')
    
    // Filter available tabs based on permissions and products
    const availableTabs = [
      ...ACCOUNT_TABS.filter(tab =>
        (!tab.requiresProduct || isProductAvailable(tab.requiresProduct)) &&
        (tab.alwaysShow || hasViewPermission(tab.resource))
      ),
      ...PRODUCT_TABS.filter(tab => 
        (tab.alwaysShow || isProductAvailable(tab.label))
      )
    ];

    const availableTabNames = availableTabs.map(tab => tab.name);
    
    // Return saved tab if it's available, otherwise return first available tab or 'Profile'
    return availableTabNames[0] || 'Profile';
  });

  const [inFocus, setInFocus] = useState<
    'organization' | 'workplace' | 'user' | 'organizational_hierarchy' | 'security_role' | 'partners' | null
  >(null);

  const [generalResetKey, setGeneralResetKey] = useState(0);

  useResetSettingsScroll(`${activeTab}:${inFocus ?? ''}`);

  // Update localStorage whenever the active tab changes
  const handleTabChange = (tab) => {
    localStorage.setItem('activeTab', tab);
    if (tab === 'General' && activeTab === 'General') {
      // User clicked General while already on General — force sub-components to remount
      setGeneralResetKey((prev) => prev + 1);
    }
    setActiveTab(tab);
    // Reset inFocus when switching tabs so any focused subsection is cleared
    setInFocus(null);
  };

  // Fetch Game Master data when GameMaster tab is clicked
  useEffect(() => {
    if (activeTab === 'GameMaster') {
      const workplaceId = getCurrentWorkplaceId();
      // Invalidate and refetch game master data
      queryClient.invalidateQueries(['gameMaster', workplaceId]);
      // Also invalidate sports preferences to refresh the selection
      queryClient.invalidateQueries(['sportsPreferences', workplaceId]);
      GetGameMasterData(workplaceId).catch((error) => {
        console.error('Error fetching Game Master data:', error);
      });
    }
  }, [activeTab, queryClient]);

  const renderContent = () => {
    // Only render a tab's content if the user has permission to view it
    const currentTab = [...ACCOUNT_TABS, ...PRODUCT_TABS].find(tab => tab.name === activeTab);
    if (!currentTab) {
      return <Typography>You do not have permission to view this section.</Typography>;
    }
    // Check product requirement
    if (currentTab.requiresProduct && !isProductAvailable(currentTab.requiresProduct)) {
      return <Typography>You do not have permission to view this section.</Typography>;
    }
    // Check view permission (skip for alwaysShow tabs)
    if (!currentTab.alwaysShow && !hasViewPermission(currentTab.resource)) {
      return <Typography>You do not have permission to view this section.</Typography>;
    }

    switch (activeTab) {
      case 'Display':
        return <DisplaySettings />;
      case 'Notifications':
        return <NotificationSetting />;
      case 'Security':
        return <SecuritySettings />;
      case 'Policy Hub':
        return <EmployeeDocuments />;
      case 'General':
        return <GeneralSettings key={generalResetKey} inFocus={inFocus} setInFocus={setInFocus} />;
      case 'Dashboard':
        return <DashBoardSetting />;
      case 'Scorecard':
        return <ScoreCardSetting />;
      case 'Guest Connect':
        return <GuestConnectSetting />;
      case 'Checklist':
        return <ChecklistSetting />;
      case 'To-Do':
        return <TodoSetting />;
      case 'Logbook':
        return <LogbooksSetting />;
      case 'Events':
        return <EventsSetting />;
      case 'Schedule':
        return <SchedulerSetting />;
      case 'Onboarding':
        return <OnboardingSetting />;
      case 'Broadcast':
        return <BoradcastSetting />;
      case 'GameMaster':
        return <GameMasterSetting />;
      default:
        return <ProfileForm />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={styles.settingsPage}>
        <Grid
          container
          columnSpacing={2}
          rowSpacing={{ xs: 2, md: 0 }}
          className={styles.settingsLayout}
        >
          <Sidebar setActiveTab={handleTabChange} activeTab={activeTab} />
          <Grid
            id={SETTINGS_SCROLL_CONTAINER_ID}
            item
            xs={12}
            md={9}
            sx={{ overflowAnchor: 'none', padding: 4 }}
            className={styles.settings}
          >
            {renderContent()}
          </Grid>
        </Grid>
      </div>
    </ThemeProvider>
  );
}